import React from 'react';
import { styles } from '../styles.ts';
import { AVAILABLE_GEMINI_MODELS } from '../../../models.ts';

interface PromptSectionProps {
  model: string;
  prompt: string;
  status: 'idle' | 'loading' | 'success' | 'error';
  errorMessage: string;
  aiMessage: string;
  onModelChange: (model: string) => void;
  onPromptChange: (prompt: string) => void;
  onUpdateLayout: () => void;
}

export function PromptSection({
  model,
  prompt,
  status,
  errorMessage,
  aiMessage,
  onModelChange,
  onPromptChange,
  onUpdateLayout
}: PromptSectionProps) {
  return (
    <section style={styles.card}>
      {/* Dynamic animations injection */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse-opacity {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}} />

      <h2 style={styles.sectionHeader}>Prompt Instructions</h2>
      
      {/* AI Model Selector */}
      <div style={styles.formGroup}>
        <label style={styles.fieldLabel}>Select AI Model:</label>
        <select
          value={model}
          onChange={(e) => onModelChange(e.target.value)}
          style={{ ...styles.select, maxWidth: '300px' }}
        >
          {AVAILABLE_GEMINI_MODELS.map(m => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.fieldLabel}>Design Prompt Instructions:</label>
        <textarea 
          id="design-prompt-textarea"
          value={prompt} 
          onChange={(e) => onPromptChange(e.target.value)} 
          style={styles.textarea} 
          rows={4}
          placeholder="e.g. Move the sofa opposite the door and clear out a space for a workspace desk near the window."
        />
      </div>
      
      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          onClick={onUpdateLayout} 
          disabled={status === 'loading'} 
          style={{ 
            ...styles.primaryButton, 
            flex: 1,
            opacity: status === 'loading' ? 0.6 : 1 
          }}
        >
          {status === 'loading' ? 'Running Layout Update...' : 'Update Room Layout'}
        </button>
      </div>

      {/* Loading indicator */}
      {status === 'loading' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px dashed var(--border-color)',
          borderRadius: '6px',
          color: 'var(--text-muted)',
          fontSize: '13px',
          animation: 'pulse-opacity 2s infinite ease-in-out'
        }}>
          <span style={{
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: '2px solid var(--text-muted)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <span>Updating layout and solving constraints with Gemini AI...</span>
        </div>
      )}

      {/* AI Message Explanation */}
      {aiMessage && status !== 'loading' && (
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(171, 71, 188, 0.06)',
          border: '1px solid rgba(171, 71, 188, 0.2)',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(171, 71, 188, 0.04)',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>✨</span>
          <div>
            <div style={{ 
              fontSize: '11px', 
              fontWeight: 600, 
              color: 'var(--accent)', 
              textTransform: 'uppercase', 
              letterSpacing: '0.05em',
              marginBottom: '4px'
            }}>
              Gemini AI Explanation
            </div>
            <div style={{ 
              fontSize: '13px', 
              color: 'var(--text-main)', 
              lineHeight: '1.4',
              fontWeight: 500
            }}>
              {aiMessage}
            </div>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div style={styles.errorAlert}>
          <strong>Error:</strong> {errorMessage}
        </div>
      )}
    </section>
  );
}
